import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { SourceCard } from '../../components/demo/SourceCard';
import { AnalysisLine } from '../../data/demo';
import { readRoleMatch, readUrlSignal, stripQuery } from '../../utils/urlSignal';
import { deriveListingTitle } from '../../utils/listingTitle';
const ADJACENT_NOTE: Record<string, string> = {
  'FIN-001': 'Same instrument: close, reconciliations, variance. Seniority calibrated at configuration.',
  'LAW-001': 'Same instrument: document review, verification, escalation. Seniority calibrated at configuration.'
};
export function RoleAnalysis() {
  const {
    postingUrl,
    postingText,
    usedSample,
    offLibrary,
    mode,
    bundle
  } = useDemo();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // A pasted URL gets read first — the system reacts to their input before anything else.
  const custom = !usedSample && (postingUrl.trim().length > 0 || postingText.trim().length > 0);
  const signal = useMemo(() => readUrlSignal(postingUrl), [postingUrl]);
  const roleMatch = useMemo(() => readRoleMatch(postingUrl), [postingUrl]);
  const adjacentNote = ADJACENT_NOTE[bundle.instrument] || ADJACENT_NOTE['FIN-001'];
  const lines: AnalysisLine[] = useMemo(() => {
    if (!custom) return bundle.analysisLines;
    // An adjacent role shows its reasoning rather than quietly asserting a match.
    const body = bundle.analysisLines.map(line => line.label === 'Role match' && roleMatch.adjacent && roleMatch.urlRole ? {
      ...line,
      values: [`${roleMatch.urlRole} → ${bundle.roleLabel}`],
      status: 'adjacent role',
      note: adjacentNote
    } : line);
    return [{
      label: 'URL signal',
      values: [signal.token],
      status: signal.matched ? 'matched' : '—'
    }, ...body];
  }, [custom, signal, roleMatch, bundle, adjacentNote]);
  useEffect(() => {
    if (step >= lines.length) return;
    const t = window.setTimeout(() => setStep(s => s + 1), step === 0 ? 450 : 800);
    return () => window.clearTimeout(t);
  }, [step, lines.length]);
  const done = step >= lines.length;
  const activePhrases = custom ? Math.max(0, step - 1) : step;
  const displayUrl = postingUrl.trim() ? stripQuery(postingUrl) : bundle.posting.url;
  const chipTitle = custom ? deriveListingTitle(postingUrl) : bundle.posting.title;
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-843" data-mp="7fj7dgv04mud">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-844" data-mp="7f1aci0im4le">
        <LenticMark data-id="element-845" data-mp="13xpa9j6ivwu2" />
        <BackLink to="/demo" label="Change the posting" data-id="element-846" data-mp="13z0a57wukkfw" />
      </div>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-8 py-10" data-id="element-847" data-mp="z6gwkay5tpb3">
        <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="inline-flex max-w-full items-center gap-3 overflow-hidden whitespace-nowrap rounded-sm border border-rule bg-white px-4 py-2.5" data-id="element-848" data-mp="2htgu7x8vj34l">
          <span className="shrink-0 font-mono-num text-[11px] text-brand" data-id="element-849" data-mp="2vti1bufqf6si">{bundle.sourceId}</span>
          <span className="h-3.5 w-px shrink-0 bg-rule" data-id="element-850" data-mp="30t4gi9f4ex3y" />
          <span className="max-w-[360px] truncate text-[13px] font-medium text-ink" data-id="element-851" data-mp="30qi8k8obifl7">
            {chipTitle}
          </span>
          <span className="min-w-0 truncate font-mono-num text-[11px] text-muted/80" data-id="element-852" data-mp="3ahme6hs3so3d">
            {displayUrl}
          </span>
          {custom && <span className="shrink-0 rounded-sm border border-rule bg-paper px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted" data-id="element-853" data-mp="3eiy46oew3bc3">
              Your listing
            </span>}
        </motion.div>

        {offLibrary && <p className="mt-4 max-w-2xl rounded-sm border-l-2 border-signal-amber bg-white px-4 py-3 text-[13px] leading-relaxed text-ink" data-id="element-854" data-mp="3auezxukxgtvo">
            This posting maps to a role family outside the current library (FIN and LAW only in this
            demo). Showing the {bundle.roleLabel} flow.
          </p>}

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,40fr)_minmax(0,60fr)]" data-id="element-855" data-mp="3fn84ahchi078">
          <SourceCard activePhrases={activePhrases} representative={custom} data-id="element-856" data-mp="2rxdsm33wihok" />

          <div data-id="element-857" data-mp="2mxrg5vvwd5gt">
            <p className="eyebrow text-brand" data-id="element-858" data-mp="2myavd4u5xfr5">Role analysis</p>

            {custom && <p className="mt-2.5 max-w-lg font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-859" data-mp="31us3ln722e9h">
                Demo maps your URL to the closest library role and analyses a representative
                posting. Production fetches and parses the posting itself.
              </p>}
            {mode === 'single' && <p className="mt-2.5 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-860" data-mp="c4wstht1ujsw">
                single-candidate probe · CV weighting applied at configuration
              </p>}

            <dl className="mt-7 divide-y divide-rule border-t border-rule" data-id="element-861" data-mp="olpkqn5adhfp">
              {lines.map((line, i) => {
              const visible = i < step;
              const isSignal = custom && i === 0;
              return <motion.div key={line.label} initial={false} animate={{
                opacity: visible ? 1 : 0.12,
                y: visible ? 0 : 4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="grid grid-cols-[180px_minmax(0,1fr)_auto] items-start gap-4 py-6" data-id="element-862" data-mp="12eoyokpf73l5" data-mpi={i}>
                    <dt className="font-mono-num text-[11px] uppercase tracking-[0.1em] text-muted" data-id="element-863" data-mp="1le2xph20b4ii">
                      {line.label}
                    </dt>
                    <dd className="space-y-1.5" data-id="element-864" data-mp="1q6ch0e8m96bt">
                      {line.values.map(v => <p key={v} className={`leading-relaxed ${isSignal ? 'font-mono-num text-[13px] text-ink' : 'text-[14px] text-ink'}`} data-id="element-865" data-mp="2008cjuf84f76">
                          {line.values.length > 1 && <span className="text-muted" data-id="element-866" data-mp="1m0qsttgsbs4o">— </span>}
                          {v}
                        </p>)}
                      {line.note && <p className="max-w-md pt-1 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-867" data-mp="1cae21jidn2q3">
                          {line.note}
                        </p>}
                    </dd>
                    <dd data-id="element-868" data-mp="138ehih85lgie">
                      {line.status === '—' ? <span className="inline-flex items-center rounded-sm border border-rule bg-paper px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted" data-id="element-869" data-mp="ya3a0d5bzi3c">
                          —
                        </span> : line.status && <span className="inline-flex items-center gap-1.5 rounded-sm border border-brand/30 bg-brand-tint px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-870" data-mp="o5mszgg47uxy">
                            <CheckIcon className="h-3 w-3" data-id="element-871" data-mp="y4vsspxnw3s8" />
                            {line.status}
                          </span>}
                    </dd>
                  </motion.div>;
            })}
            </dl>

            <motion.div initial={false} animate={{
            opacity: done ? 1 : 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-872" data-mp="aei150izo6y6">
              <p className="mt-7 max-w-lg text-[13.5px] leading-relaxed text-muted" data-id="element-873" data-mp="3dw06907dmzuw">
                Cohort assessments select from the library. Tailored to CV assessments are created for single candidates.
              </p>
              <button type="button" onClick={() => navigate('/demo/configure')} disabled={!done} className="group mt-8 inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark disabled:cursor-not-allowed" data-id="element-874" data-mp="10x8eo8zfc7tb">
                Configure the assessment
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-875" data-mp="1fuhb3h3l41rw" />
              </button>
            </motion.div>
          </div>
        </div>
      </main>

      <DemoFooter data-id="element-876" data-mp="1om189yskq3kr" />
    </div>;
}